import {
  AuthCredential,
  getAdditionalUserInfo,
  UserCredential
} from "firebase/auth";

import { Dispatch, GetState } from "common/types";

import {
  signInWithProvider as authWithProvider,
  signOut as signOutFromFirebase
} from "services/firebase/auth";
import { pushProviderUserIdToGlobalDataLayer } from "services/firebase/auth/analytics/events";
import {
  AccountExistsWithDifferentCredential,
  GoStudentSubscriptionInvalid
} from "services/firebase/auth/errors";
import { ProviderSSO } from "services/firebase/auth/types";

import {
  getRegion,
  signInError,
  signInStart
} from "seneca-common/features/user/state";

import {
  attemptFirstMISLogin,
  proceedUserSignIn,
  proceedUserSignUp
} from "./utils";
import extractUserDetailsWithLinkedProviders from "./utils/extractUserDetailsWithLinkedProviders";
import isGoStudentSubscriptionInToken from "./utils/isGoStudentSubscriptionInToken";
import isUserSignedUp from "./utils/isUserSignedUp";

type Args = {
  providerName: ProviderSSO;
  signInWithRedirect?: boolean;
  onSignInSuccess: () => void;
  onSignUpSuccess: () => void;
  onPasswordConfirmation: (info: {
    credential: AuthCredential;
    email: string;
  }) => void;
};

export default function signInWithProvider({
  providerName,
  signInWithRedirect,
  onSignInSuccess,
  onSignUpSuccess,
  onPasswordConfirmation
}: Args) {
  return async function (dispatch: Dispatch, getState: GetState) {
    const region = getRegion(getState())!;

    try {
      dispatch(signInStart());

      const authInfo = (await authWithProvider(
        providerName,
        signInWithRedirect
      )) as UserCredential;

      const firebaseUserInfo = getAdditionalUserInfo(authInfo);

      pushProviderUserIdToGlobalDataLayer(
        authInfo.user.providerData?.find(
          provider => provider.providerId === authInfo.providerId
        )?.uid
      );

      let signUp = !!firebaseUserInfo?.isNewUser;
      if (providerName === "gostudent") {
        if ((await isGoStudentSubscriptionInToken()) === false) {
          await signOutFromFirebase();
          throw new GoStudentSubscriptionInvalid();
        }

        if ((await isUserSignedUp()) === false) {
          signUp = true;
        }
      }

      if (signUp) {
        const userDetails = extractUserDetailsWithLinkedProviders({
          authInfo,
          firebaseUserInfo
        });

        await dispatch(
          proceedUserSignUp({
            userInfoToSave: userDetails,
            onSignUpSuccess,
            region
          })
        );
      } else {
        await dispatch(
          proceedUserSignIn({
            onSignInSuccess,
            region,
            userId: authInfo.user.uid
          })
        );
      }
    } catch (error: any) {
      if (error instanceof AccountExistsWithDifferentCredential) {
        const { credential, email } = error;

        await dispatch(
          attemptFirstMISLogin({
            region,
            onSignInSuccess,
            providerName,
            credential,
            email,
            onPasswordConfirmation
          })
        );

        return;
      }

      dispatch(signInError());
      throw error;
    }
  };
}
